import { idbReq, idbTxDone, openOfflineDb } from './idb';
import { computeBackoffMs, newIdempotencyKey } from './queuePolicy';
import type { OutboxItem, OutboxStatus } from './types';

export interface EnqueueInput {
  platform: string;
  method: string;
  path: string;
  body?: string | null;
  entityKey?: string;
  idempotencyKey?: string;
}

export async function enqueueMutation(input: EnqueueInput): Promise<OutboxItem> {
  const db = await openOfflineDb(input.platform);
  const item: OutboxItem = {
    id: newIdempotencyKey(),
    platform: input.platform,
    method: (input.method || 'POST').toUpperCase(),
    path: input.path,
    body: input.body ?? null,
    idempotencyKey: input.idempotencyKey || newIdempotencyKey(),
    createdAt: Date.now(),
    attempts: 0,
    nextAttemptAt: Date.now(),
    status: 'pending',
    entityKey: input.entityKey,
  };
  const tx = db.transaction('outbox', 'readwrite');
  tx.objectStore('outbox').put(item);
  await idbTxDone(tx);
  db.close();
  return item;
}

export async function listOutbox(
  platform: string,
  statuses: OutboxStatus[] = ['pending', 'failed', 'syncing'],
): Promise<OutboxItem[]> {
  const db = await openOfflineDb(platform);
  const tx = db.transaction('outbox', 'readonly');
  const all = await idbReq(tx.objectStore('outbox').getAll());
  db.close();
  const allow = new Set(statuses);
  return (all as OutboxItem[])
    .filter((i) => allow.has(i.status))
    .sort((a, b) => a.createdAt - b.createdAt);
}

export async function countPending(platform: string): Promise<number> {
  const items = await listOutbox(platform, ['pending', 'failed', 'syncing']);
  return items.length;
}

export async function updateOutboxItem(
  platform: string,
  id: string,
  patch: Partial<OutboxItem>,
): Promise<void> {
  const db = await openOfflineDb(platform);
  const tx = db.transaction('outbox', 'readwrite');
  const store = tx.objectStore('outbox');
  const existing = (await idbReq(store.get(id))) as OutboxItem | undefined;
  if (!existing) {
    db.close();
    return;
  }
  store.put({ ...existing, ...patch, id });
  await idbTxDone(tx);
  db.close();
}

export async function removeOutboxItem(platform: string, id: string): Promise<void> {
  const db = await openOfflineDb(platform);
  const tx = db.transaction('outbox', 'readwrite');
  tx.objectStore('outbox').delete(id);
  await idbTxDone(tx);
  db.close();
}

export async function clearOutbox(platform: string): Promise<void> {
  const db = await openOfflineDb(platform);
  const tx = db.transaction('outbox', 'readwrite');
  tx.objectStore('outbox').clear();
  await idbTxDone(tx);
  db.close();
}

export async function markAttemptFailure(
  platform: string,
  item: OutboxItem,
  errorMessage: string,
): Promise<void> {
  const attempts = item.attempts + 1;
  await updateOutboxItem(platform, item.id, {
    attempts,
    status: attempts >= 12 ? 'failed' : 'pending',
    nextAttemptAt: Date.now() + computeBackoffMs(attempts),
    lastError: errorMessage.slice(0, 500),
  });
}
